import { atom, useAtom } from "jotai";
import { DateTime } from "luxon";
import React from "react";
import { toast } from "react-toastify";
import { modalActions } from "../../../../../../context/actions/modalActions";
import useStore from "../../../../../../context/useStore";
import dataService from "../../../../../../helpers/dataService";
import { clientAtom } from "../useClientItem";
import UserForm from "./UserForm";

const isSendingActivationAtom = atom(false);
const isUnlockingAtom = atom(false);
const isActivatingAtom = atom(false);
const errorsAtom = atom({});
const isBlockUserAtom = atom(false);
const isBlockingAtom = atom(false);
const isAvisGoogleAtom = atom(false);
const isSendingInvitationAtom = atom(false);

const useUserInfos = () => {
  const [client, setClient] = useAtom(clientAtom);
  const [isSendingActivation, setIsSendingActivation] = useAtom(
    isSendingActivationAtom
  );
  const [isActivating, setIsActivating] = useAtom(isActivatingAtom);
  const [isUnlocking, setIsUnlocking] = useAtom(isUnlockingAtom);
  const [isSendingAvisGoogle, setIsAvisGoogle] = useAtom(isAvisGoogleAtom);
  const [isSendingInvitation, setIsSendingInvitation] = useAtom(
    isSendingInvitationAtom
  );
  const [isBlocking, setIsBlocking] = useAtom(isBlockingAtom);
  const [errors, setErrors] = useAtom(errorsAtom);
  const [isBlockUser, setIsBlockUser] = useAtom(isBlockUserAtom);

  const [state, dispatch] = useStore();
  const mActions = modalActions(state, dispatch);

  const sendActivation = () => {
    setIsSendingActivation(true);
    dataService.get(
      `customers/${client.id}/email_activation`,
      (data) => {
        setClient({
          ...client,
          activationTokenSendedAt: DateTime.local().toISO(),
        });
        toast.success("Email d'activation envoyé");
      },
      (err) => {},
      () => setIsSendingActivation(false)
    );
  };

  const sendAvisGoogle = () => {
    setIsAvisGoogle(true);
    dataService.get(
      `customers/${client.id}/avis-google`,
      (data) => {
        const { firstname, lastname } = state.auth.user;
        setClient({
          ...client,
          emailAvisGoogleSentAt: DateTime.local().toISO(),
          emailAvisGoogleSentByNom: [firstname, lastname]
            .filter(Boolean)
            .join(" "),
        });
        toast.success("Email avis Google envoyé");
      },
      (err) => {},
      () => setIsAvisGoogle(false)
    );
  };

  // PER-2398 — invitation à créer son mot de passe (comptes sans mot de passe).
  // Les erreurs 409 / 429 / 404 ({message}) sont affichées en toast par dataService.
  const sendInvitation = () => {
    if (!window.confirm(`Envoyer l'invitation à ${client.email} ?`)) {
      return;
    }
    setIsSendingInvitation(true);
    dataService.post(
      `customers/${client.id}/invitation`,
      {},
      (data) => {
        const { firstname, lastname } = state.auth.user;
        setClient({
          ...client,
          invitationSentAt: (data && data.sentAt) || DateTime.local().toISO(),
          invitationSentByNom: [firstname, lastname].filter(Boolean).join(" "),
        });
        toast.success("Invitation envoyée");
      },
      () => {},
      () => setIsSendingInvitation(false)
    );
  };

  const unlockAccount = () => {
    setIsUnlocking(true);
    dataService.get(
      `customers/${client.id}/authorization/reset`,
      (data) => {
        toast.success("Le mot de passe de l'utilsateur a été réinitialisé");
      },
      setErrors,
      () => setIsUnlocking(false)
    );
  };

  const activateMail = () => {
    setIsActivating(true);
    dataService.patch(
      `customers/${client.id}`,
      { ...client, isActivated: true },
      (data) => {
        setClient({ ...client, isActivated: true });
        toast.success("Le compte de l'utilisateur a bien été activé");
      },
      setErrors,
      () => setIsActivating(false)
    );
  };

  const blockUserAccount = () => {
    if (
      window.confirm(
        `Souhaitez-vous ${
          isBlockUser ? "débloquer" : "bloquer"
        } cet utilisateur ?`
      )
    ) {
      setIsBlocking(true);
      if (isBlockUser) {
        setIsBlockUser(false);
        dataService.patch(
          `customers/${client.id}/update-lock-status`,
          {},
          toast.success(`Le compte de l'utilisateur a bien été débloqué`),

          setErrors
        );
      } else {
        setIsBlockUser(true);
        dataService.patch(
          `customers/${client.id}/update-lock-status`,
          {},
          toast.success(`Le compte de l'utilisateur a bien été bloqué`),

          setErrors
        );
      }
      setIsBlocking(false);
    }
  };

  const updateClient = (newClient) => {
    setClient(newClient);
  };

  const openUserForm = () => {
    mActions.updateModal({
      isOpen: true,
      content: <UserForm client={client} updateClient={updateClient} />,
    });
  };

  return {
    client,
    isSendingActivation,
    isSendingAvisGoogle,
    isSendingInvitation,
    errors,
    isActivating,
    isUnlocking,
    isBlockUser,
    isBlocking,
    actions: {
      sendActivation,
      sendAvisGoogle,
      sendInvitation,
      unlockAccount,
      activateMail,
      updateClient,
      openUserForm,
      blockUserAccount,
    },
  };
};

export default useUserInfos;
